var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Lottie from "react-lottie";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { createUseStyles } from "react-jss";
import Typography from "../Typography";
import { useGlobalContext } from "../Context";
import { defaultLottieOptions } from "../common/defaultLottieOptions";
import { replaceVerbiage } from "../../utils/replaceVerbiage";
import { dispatchEvent } from "../../utils/analytics";
var useStyles = createUseStyles(function (theme) {
    var _a, _b, _c, _d, _e;
    var getVendorColor = function (props, option) {
        var activeStyles = props.vendorStyles.vendors.find(function (currentVendor) { return currentVendor.vendor === props.vendor; });
        return activeStyles ? activeStyles[option] : null;
    };
    var size = 24;
    return {
        root: {
            display: "block",
            position: "relative",
            overflow: "hidden",
            width: "100%",
            margin: "0 auto",
            backgroundColor: function (props) {
                return props.variant === "Solid"
                    ? getVendorColor(props, "backgroundColor")
                    : theme.colors.white;
            },
        },
        container: (_a = {
                display: "flex",
                position: "relative",
                overflow: "hidden",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                maxWidth: theme.maxWidth,
                margin: "0 auto",
                padding: "80px 35px",
                backgroundColor: function (props) {
                    return props.variant === "Solid"
                        ? getVendorColor(props, "backgroundColor")
                        : theme.colors.white;
                },
                color: function (props) {
                    return getVendorColor(props, "textColor");
                }
            },
            _a[theme.mediaQuery(theme.breakpoints.small)] = {
                flexDirection: "row",
                padding: "80px 60px",
            },
            _a[theme.mediaQuery(theme.breakpoints.medium)] = {
                padding: "80px 120px",
            },
            _a),
        bandedRight: {
            "&:before": (_b = {
                    display: "block",
                    position: "absolute",
                    content: "''",
                    width: "100%",
                    height: "25%",
                    bottom: 0,
                    right: 0,
                    backgroundColor: function (props) {
                        return getVendorColor(props, "backgroundColor");
                    }
                },
                _b[theme.mediaQuery(theme.breakpoints.small)] = {
                    width: "20%",
                    height: "100%",
                },
                _b),
        },
        bandedLeft: {
            "&:before": (_c = {
                    display: "block",
                    position: "absolute",
                    content: "''",
                    width: "100%",
                    height: "25%",
                    bottom: 0,
                    left: 0,
                    backgroundColor: function (props) {
                        return getVendorColor(props, "backgroundColor");
                    }
                },
                _c[theme.mediaQuery(theme.breakpoints.small)] = {
                    width: "20%",
                    height: "100%",
                },
                _c),
        },
        image: (_d = {
                display: "flex",
                position: "relative",
                justifyContent: "center",
                alignItems: "center",
                order: 2,
                flex: "0 1 auto",
                "& img": {
                    display: "block",
                    margin: "0 auto",
                    width: "100%",
                    maxWidth: 520,
                    height: "auto",
                }
            },
            _d[theme.mediaQuery(theme.breakpoints.small)] = {
                order: function (props) {
                    return props.imagePosition === "Right" ? 2 : 0;
                },
                flex: function (props) {
                    return props.layout === "Half" ? "0 1 50%" : "0 1 25%";
                },
            },
            _d),
        content: (_e = {
                flex: "0 1 auto",
                textAlign: "left",
                "& p": {
                    marginBottom: 44,
                }
            },
            _e[theme.mediaQuery(theme.breakpoints.small)] = {
                flex: function (props) {
                    return props.layout === "Half" ? "0 1 50%" : "0 1 75%";
                },
                padding: "0 15px",
            },
            _e["& h3"] = {
                fontSize: theme.typography.heading3.fontSize,
                lineHeight: theme.typography.heading3.lineHeight,
                fontWeight: theme.typography.heading3.fontWeight,
                marginBottom: "27px",
            },
            _e["& ol"] = {
                listStyle: "none",
                counterReset: "ordered-list-counter",
                marginLeft: 35,
                marginBottom: "2.5rem",
                "& li": {
                    paddingBottom: "1.5rem",
                    counterIncrement: "ordered-list-counter",
                    position: "relative",
                    "& p": {
                        marginBottom: 0,
                    },
                    "&:before": {
                        content: "counter(ordered-list-counter)",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        position: "absolute",
                        left: "calc(-1 * " + size + "px - 10px)",
                        lineHeight: size + "px",
                        width: size,
                        height: size,
                        top: "4px",
                        color: function (props) {
                            return getVendorColor(props, "backgroundColor");
                        },
                        backgroundColor: function (props) {
                            return getVendorColor(props, "textColor");
                        },
                        borderRadius: "50%",
                        textAlign: "center",
                    },
                },
            },
            _e["& ul"] = {
                listStyle: "disc",
                marginLeft: 35,
                marginBottom: "2.5rem",
                "& li": {
                    paddingBottom: "1.5rem",
                    fontSize: "medium",
                    position: "relative",
                    "& p": {
                        marginBottom: 0,
                    },
                },
            },
            _e),
        heading: {
            marginBottom: 44,
        },
        tabbedContent: {
            display: "flex",
            justifyContent: "space-around",
            fontSize: 14,
            lineHeight: "150%",
            marginBottom: 27,
        },
        tab: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
            borderBottom: "3px solid #D5D6DA",
            textAlign: "center",
            "&:after": {
                content: "''",
                height: 3,
                backgroundColor: "transparent",
            },
            "& button": {
                apperance: "none",
                border: 0,
                "&:focus": {
                    outline: 0,
                },
            },
        },
        activeTab: {
            borderBottom: function (props) {
                return "6px solid " + (props.tabbedContentAccentColor
                    ? props.tabbedContentAccentColor
                    : theme.colors.asurion.purple);
            },
        },
    };
});
var Media = function (_a) {
    var _b;
    var mainContent = _a.mainContent, headline = _a.headline, imagePosition = _a.imagePosition, variant = _a.variant, layout = _a.layout, vendorStyles = _a.vendorStyles, useTabbedContent = _a.useTabbedContent, tabbedContent = _a.tabbedContent, tabbedContentAccentColor = _a.tabbedContentAccentColor;
    var _c = useGlobalContext(), vendor = _c.vendor, article = _c.article;
    var classes = useStyles({
        vendor: vendor,
        variant: variant,
        imagePosition: imagePosition,
        layout: layout,
        vendorStyles: vendorStyles,
        tabbedContent: tabbedContent,
        tabbedContentAccentColor: tabbedContentAccentColor,
    });
    var _d = useState(tabbedContent.length > 0 ? tabbedContent[0] : {}), activeTab = _d[0], setActiveTab = _d[1];
    var updateTab = function (tab) {
        var newTab = tabbedContent.find(function (entry) { return entry === tab; });
        if (newTab) {
            setActiveTab(newTab);
            dispatchEvent("Tab_Clicked", __assign(__assign({}, article), { tabTitle: newTab.fields.title }));
        }
    };
    var activeVendor = vendorStyles.vendors.find(function (currentVendor) { return currentVendor.vendor === vendor; });
    if (!activeVendor) {
        return null;
    }
    var image = activeVendor.image, lottieAnimation = activeVendor.lottieAnimation;
    var renderedContent = mainContent
        ? replaceVerbiage(mainContent, vendor)
        : "";
    return (React.createElement("div", { className: classes.root },
        React.createElement("div", { className: classNames((_b = {},
                _b[classes.container] = true,
                _b[classes.bandedRight] = variant === "Banded" && imagePosition === "Right",
                _b[classes.bandedLeft] = variant === "Banded" && imagePosition === "Left",
                _b)) },
            React.createElement("div", { className: classes.image },
                React.createElement("div", null, !lottieAnimation ? (React.createElement("img", { src: ((image && image.fields.file.url) ||
                        (image && image.fields.file["en-US"].url)) + "?w=520&h=740", alt: "", role: "presentation" })) : (React.createElement(Lottie, { options: __assign({ animationData: JSON.parse(lottieAnimation) }, defaultLottieOptions), height: 400, width: 400 })))),
            React.createElement("div", { className: classes.content },
                React.createElement(Typography, { variant: "heading2", className: classes.heading }, headline),
                React.createElement("div", { dangerouslySetInnerHTML: {
                        __html: renderedContent,
                    } }),
                useTabbedContent && tabbedContent.length > 0 ? (React.createElement("div", null,
                    React.createElement("div", { className: classes.tabbedContent },
                        React.createElement(React.Fragment, null, tabbedContent.map(function (tab, index) {
                            var _a;
                            return (React.createElement("div", { key: index, className: classNames((_a = {},
                                    _a[classes.tab] = true,
                                    _a[classes.activeTab] = activeTab === tab,
                                    _a)) },
                                React.createElement("button", { onClick: function () { return updateTab(tab); } }, tab.fields.title)));
                        }))),
                    React.createElement("div", null, documentToReactComponents(activeTab.fields.tabContent)))) : null))));
};
Media.defaultProps = {
    layout: "Half",
    variant: "Solid",
    vendorStyles: {
        vendors: [
            {
                vendor: "asurion",
                backgroundColor: "#8223D2",
                textColor: "#fff",
            },
        ],
    },
    tabbedContent: [],
};
Media.propTypes = {
    mainContent: PropTypes.any.isRequired,
    headline: PropTypes.string.isRequired,
    imagePosition: PropTypes.oneOf(["Left", "Right"]).isRequired,
    layout: PropTypes.oneOf(["Half", "Full Text"]).isRequired,
    variant: PropTypes.oneOf(["Solid", "Banded"]).isRequired,
    vendorStyles: PropTypes.any.isRequired,
    useTabbedContent: PropTypes.bool,
};
export default Media;
